import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery, useMutation } from '@apollo/client/react/hooks';
import Styled from 'styled';
import { toGlobalId } from 'graphql-relay';
import { Link } from 'react-router-dom';
import Helmet from 'helmet/helmet';

import map from 'lib/react/html/map';
import Img from 'lib/images/image-view';
import LoadingProgress from 'lib/react-router/loading-progress';
import ScrollToTop from 'lib/scroll/scroll-to-top';
import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import loadScript, { appendScript } from 'lib/script/script';

import { SiteContext } from 'four-nets';
import Description from 'four-nets/photoblog/posts/album/detail/description/description';
import Footer from 'four-nets/photoblog/posts/common/footer/footer';
import ErrorPage from 'four-nets/errors/error-page';
import MostPopularPbAlbums from 'four-nets/widgets/most-popular-pb-albums/most-popular-pb-albums';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import Comments from 'four-nets/photoblog/posts/common/comments/comments';

import mutation, {
    SetIsPublishedPrMutation,
    SetIsPublishedPrMutationVariables,
} from './mutations/set-is-published-pr.graphql';

import articleQuery, { ArticleBySlugQuery, ArticleBySlugQueryVariables } from './article.graphql';
import defaultStyles from './article.scss';

interface Params {
    slug: string;
    username: string;
}

export interface Props extends RouteWithSearchRenderProps<Params> {
    styles: typeof defaultStyles;
    slug: string;
}

const Article: React.FC<Props> = props => {
    const { styles, location, match } = props;
    const { slug, username } = match.params;
    const { hash } = location;

    const { viewer } = useContext(ViewerContext);
    const site = useContext(SiteContext);

    const matches = hash.match(/#comment-(\d+)/);
    let commentId: string | undefined;
    if (matches) {
        commentId = toGlobalId('CommentNode', matches[1]!);
    }

    const isPrArticle = /blog\/(pr_clanok|pr_clanek|modrastrecha_sk|modrastrecha_cz)\//.test(location.pathname);

    const { data, loading, error, refetch } = useQuery<ArticleBySlugQuery, ArticleBySlugQueryVariables>(articleQuery, {
        variables: { slug, username },
    });
    const [setIsPublishedPr] = useMutation<SetIsPublishedPrMutation, SetIsPublishedPrMutationVariables>(mutation);

    const scripts = useRef<Array<{ name: string; props: any; children: any }>>([]);
    const scriptRef = useRef<HTMLSpanElement>(null);
    const [loaded, setLoaded] = useState(false);

    const article = !error && data && data.postBySlug;

    const renderHelmet = (articleTitle: string) => (H: React.ComponentClass) => {
        const title = `${articleTitle}${site ? ` | ${site.name}` : ''}`;
        return (
            <H>
                <title>{title}</title>,
                <meta property='og:title' content={title} />
            </H>
        );
    };

    useEffect(() => {
        refetch();
    }, [viewer, refetch]);

    useEffect(() => {
        if (!loaded && scripts.current.length) {
            const scriptsWithSrc: Promise<any>[] = [];
            const scriptsWithoutSrc: any[] = [];

            scripts.current.forEach(({ name, props, children }) => {
                if (props.src) {
                    scriptsWithSrc.push(loadScript(props.src));
                } else if (children) {
                    scriptsWithoutSrc.push(children);
                }
            });

            Promise.all(scriptsWithSrc).then(() => {
                scriptsWithoutSrc.forEach(children => {
                    appendScript(children, scriptRef.current);
                });
            });

            setLoaded(true);
        }
    }, [loaded]);

    if (!loading && error) {
        const gqlError = error?.graphQLErrors[0];
        if (gqlError?.extensions?.code == 401) {
            (error as any).code = 401;
        } else {
            (error as any).code = 404;
        }
        return <ErrorPage error={error} />;
    }
    return (
        <React.Fragment>
            {commentId ? null : <ScrollToTop />}
            <LoadingProgress loading={loading}>
                <ErrorPage error={error}>
                    {article && article.user ? (
                        <div className={styles.columns}>
                            <Helmet render={renderHelmet(article.title!)} />
                            <div>
                                <Description {...article} />
                                {viewer && viewer.username === article.user.username ? (
                                    <div className={styles.row}>
                                        <div className={styles.editButtonWrapper}>
                                            <Link
                                                to={match.url.endsWith('/') ? `${match.url}edit` : `${match.url}/edit`}
                                            >
                                                <BigButtonView label={<FormattedMessage id='EDIT_ARTICLE' />} />
                                            </Link>
                                        </div>
                                        {article.canPublishPr ? (
                                            <button
                                                className={styles.publishButton}
                                                onClick={() => {
                                                    setIsPublishedPr({
                                                        variables: {
                                                            input: { id: article.id, value: !article.isPublishedPr },
                                                        },
                                                    });
                                                }}
                                            >
                                                {article.isPublishedPr ? (
                                                    <FormattedMessage id='UNPUBLISH_PR' />
                                                ) : (
                                                    <FormattedMessage id='PUBLISH_PR' />
                                                )}
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}
                                <article className={styles.article}>
                                    {map(
                                        article.content,
                                        (
                                            name: string,
                                            props: React.ImgHTMLAttributes<HTMLImageElement>,
                                            children: any
                                        ) => {
                                            if (name === 'img') {
                                                const { id: key, alt, src, height, width } = props;

                                                return (
                                                    <Img
                                                        key={key}
                                                        alt={alt}
                                                        linkify={false}
                                                        sizes={{ xs: 320, md: 500 }}
                                                        src={src || ''}
                                                        scaleDown={true}
                                                        noBackground={true}
                                                        className={styles.image}
                                                        height={height}
                                                        width={width}
                                                    />
                                                );
                                            } else if (name === 'script' && isPrArticle) {
                                                scripts.current.push({ name, props, children });

                                                return props.src ? null : (
                                                    <span
                                                        ref={scriptRef}
                                                        className='custom-javascript-container'
                                                    ></span>
                                                );
                                            } else if (name === 'style' && isPrArticle) {
                                                return <style dangerouslySetInnerHTML={{ __html: children }} />;
                                            }
                                        }
                                    )}
                                </article>
                                <React.Fragment>
                                    <Footer {...article} isPrArticle={isPrArticle} inFeed={true} />
                                    {isPrArticle ? null : (
                                        <Comments variables={{ parentId: article.id }} commentId={commentId} />
                                    )}
                                </React.Fragment>
                            </div>
                            <aside className={styles.rightColumn}>
                                <MostPopularPbAlbums style={{ wrapper: styles.mostPopular }} />
                            </aside>
                        </div>
                    ) : null}
                </ErrorPage>
            </LoadingProgress>
        </React.Fragment>
    );
};

export default Styled(defaultStyles)(Article);
